<script setup lang="ts">
import { IconError, type ImageInfo } from '#shared'
import AttachmentLoader from './attachment-loader.vue'
import AttachmentRemoveButton from './attachment-remove-button.vue'
import AttachmentReuploadButton from './attachment-reupload-button.vue'

const { uploadState = 'finished', imageInfo } = defineProps<{
  uploadState?: 'pending' | 'finished' | 'errored'
  imageInfo: ImageInfo
}>()

const emit = defineEmits<{
  delete: []
  reupload: []
}>()

const onDelete = () => emit('delete')
const onReupload = () => emit('reupload')
</script>

<template>
  <div :class="$style.root">
    <img :class="$style.img" :src="imageInfo.src" :alt="imageInfo.alt" />

    <div :class="$style.actions">
      <AttachmentReuploadButton v-if="uploadState === 'errored'" :class="$style.reupload" @click="onReupload" />
      <AttachmentRemoveButton :class="$style.remove" @click="onDelete" />
    </div>

    <AttachmentLoader v-if="uploadState === 'pending'" :class="$style.loader" />

    <div v-if="uploadState === 'errored'" :class="$style.error">
      <IconError :class="$style.errorIcon" />
      <div :class="$style.errorText">Failed to upload</div>
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;

  &:not(:hover) {
    .actions {
      opacity: 0;
    }
  }
}

.img {
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.actions {
  position: absolute;
  top: 5px;
  right: 5px;
  width: fit-content;
  display: flex;
  gap: 5px;
  transition: opacity 0.2s;
}

.loader {
  position: absolute;
  left: 5px;
  right: 5px;
  bottom: 5px;
}

.error {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 5px;
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: #303035dd;
}

.errorIcon {
  flex-shrink: 0;
}

.errorText {
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
}
</style>
